import * as Sentry from '@sentry/react';
import { useEffect } from 'react';
import {
  useLocation,
  useNavigationType,
  createRoutesFromChildren,
  matchRoutes,
} from 'react-router-dom';

const PII_FIELDS = new Set([
  // Authentication & Secrets
  'password',
  'user_password',
  'otp',
  'newPassword',
  'passphrase',
  'token',
  'secret',
  'auth_token',
  'authorization',
  'jwt',
  'cookie',
  'session',
  'apiKey',
  'api_key',
  'access_token',
  'refresh_token',

  // Financial
  'cvv',
  'card_number',
  'credit_card',
  'stripe_secret_key',
  'payment_id',
  'account_number',
  'routing_number',

  // Personal Identification
  'email',
  'user_email',
  'phone',
  'phone_number',
  'address',
  'dob',
  'birth_date',
  'ssn',
  'tax_id',
  'qrcode_key',
  'id_number',
]);

function scrubPII(obj, depth = 0) {
  if (depth > 8 || !obj || typeof obj !== 'object') return obj;
  const clone = Array.isArray(obj) ? [] : {};
  for (const key of Object.keys(obj)) {
    if (
      PII_FIELDS.has(key) ||
      [...PII_FIELDS].some((f) => key.toLowerCase().includes(f.toLowerCase()))
    ) {
      clone[key] = '[REDACTED]';
    } else if (typeof obj[key] === 'object') {
      clone[key] = scrubPII(obj[key], depth + 1);
    } else {
      clone[key] = obj[key];
    }
  }
  return clone;
}

export const provider = {
  init: () => {
    const isEnabled = process.env.REACT_APP_MONITOR_ENABLED === 'true';
    const dsn = process.env.REACT_APP_MONITOR_DSN;

    if (!isEnabled || !dsn) return;

    Sentry.init({
      dsn: dsn,
      environment: process.env.REACT_APP_MONITOR_ENV || 'development',
      integrations: [
        Sentry.reactRouterV6BrowserTracingIntegration({
          useEffect,
          useLocation,
          useNavigationType,
          createRoutesFromChildren,
          matchRoutes,
        }),
        Sentry.replayIntegration(),
      ],
      tracesSampleRate: parseFloat(process.env.REACT_APP_MONITOR_SAMPLE_RATE || '0.1'),
      replaysSessionSampleRate: 0.1,
      replaysOnErrorSampleRate: 1.0,
      beforeSend(event) {
        // Scrub PII from Breadcrumbs
        if (event.breadcrumbs) {
          event.breadcrumbs.forEach((breadcrumb) => {
            if (breadcrumb.data) {
              breadcrumb.data = scrubPII(breadcrumb.data);
            }
          });
        }
        return event;
      },
    });
  },

  captureError: (err, extra = {}) => {
    Sentry.captureException(err, { extra: scrubPII(extra) });
  },

  captureMessage: (msg, level = 'info') => {
    Sentry.captureMessage(msg, level);
  },

  ErrorBoundary: Sentry.ErrorBoundary,
};
